import { forwardRef } from 'react'
import { Box, InputAdornment, TextField } from '@mui/material'
import { Search } from '@mui/icons-material'
import { SEARCH_TRIGGER_RADIUS_DESKTOP, SEARCH_TRIGGER_RADIUS_MOBILE } from 'core/theme.tokens'

interface CommandPaletteTriggerProps {
  isMobile: boolean
  onOpen: () => void
  highlighted?: boolean
}

const shortcutHint = () => {
  if (typeof navigator === 'undefined') return '⌘K'
  return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ K' : 'Ctrl K'
}

export const CommandPaletteTrigger = forwardRef<HTMLDivElement, CommandPaletteTriggerProps>(
  function CommandPaletteTrigger({ isMobile, onOpen, highlighted = false }, ref) {
    const highlightSx = highlighted
      ? {
          position: 'relative' as const,
          zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 1,
          borderRadius: 2,
        }
      : null

    return (
      <Box
        ref={ref}
        data-feature-demo-anchor='command-palette'
        sx={[
          {
            width: '100%',
            maxWidth: 'none',
            minWidth: 0,
            flex: '1 1 0%',
          },
          highlightSx,
        ]}
      >
        <TextField
          size='small'
          fullWidth
          placeholder={isMobile ? 'Search' : 'Search the portal'}
          onClick={onOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onOpen()
            }
          }}
          value=''
          autoComplete='off'
          spellCheck={false}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' htmlColor='inherit' aria-hidden />
              </InputAdornment>
            ),
            endAdornment: isMobile ? undefined : (
              <InputAdornment position='end'>
                <Box
                  component='span'
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0.75,
                    px: 0.75,
                    py: 0.15,
                    lineHeight: 1.4,
                    userSelect: 'none',
                  }}
                >
                  {shortcutHint()}
                </Box>
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': isMobile ? 'Open search' : 'Open command palette',
            readOnly: true,
          }}
          sx={{
            width: '100%',
            maxWidth: 'none',
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              cursor: 'pointer',
              borderRadius: isMobile ? SEARCH_TRIGGER_RADIUS_MOBILE : SEARCH_TRIGGER_RADIUS_DESKTOP,
              minHeight: isMobile ? 36 : 40,
              maxHeight: isMobile ? 36 : 40,
              transition: 'box-shadow 120ms ease, border-color 120ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              '&:hover': {
                boxShadow: 1,
              },
              '&.Mui-focused': {
                boxShadow: 2,
              },
            },
            '& .MuiInputBase-input': {
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: 0,
              height: isMobile ? 36 : 40,
              boxSizing: 'border-box',
              py: 0,
              px: isMobile ? 0.5 : undefined,
              fontSize: 14,
            },
          }}
        />
      </Box>
    )
  },
)
